import React from 'react';
import Navigation from '../../components/layout/Navigation';

const CoppaRightsPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navigation isAuthenticated={false} />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-display font-bold text-rowing-gray-900 mb-4">
          COPPA Rights for Parents
        </h1>
        <p className="text-xl text-rowing-gray-600 mb-8">
          Your rights under the Children's Online Privacy Protection Act
        </p>

        <div className="prose prose-lg max-w-none">
          <div className="bg-rowing-blue bg-opacity-10 border border-rowing-blue rounded-lg p-6 mb-8">
            <h2 className="text-xl font-semibold mb-3">Quick Summary</h2>
            <p className="mb-4">
              As a parent or guardian of a Glyyd user under 18, you have the right to:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Review your child's personal information</li>
              <li>Request deletion of your child's data</li>
              <li>Refuse further collection or use of your child's information</li>
              <li>Withdraw consent for specific data uses (NEW as of June 2025)</li>
            </ul>
          </div>

          <h2 className="text-2xl font-bold text-rowing-gray-900 mt-8 mb-4">Understanding COPPA</h2>
          <p className="mb-4">
            The Children's Online Privacy Protection Act (COPPA) gives parents control over what
            information websites and online services can collect from children under 13. Glyyd
            extends these protections to all users under 18.
          </p>

          <h3 className="text-xl font-semibold mt-6 mb-3">Information We Collect</h3>
          <p className="mb-4">With your consent, we collect:</p>
          <ul className="list-disc pl-6 space-y-2 mb-6">
            <li>
              <strong>Account Information:</strong> Name, email, date of birth
            </li>
            <li>
              <strong>Athletic Data:</strong> ERG scores, rowing achievements, team affiliations
            </li>
            <li>
              <strong>Academic Information:</strong> Self-reported GPA and test scores
            </li>
            <li>
              <strong>Media:</strong> Photos and videos for recruitment profiles
            </li>
            <li>
              <strong>Communications:</strong> Messages with coaches (when permitted)
            </li>
            <li>
              <strong>Usage Data:</strong> How your child uses the platform
            </li>
          </ul>

          <h3 className="text-xl font-semibold mt-6 mb-3">Your Parental Rights</h3>

          <div className="bg-gray-50 rounded-lg p-6 mb-6">
            <h4 className="text-lg font-semibold mb-3">1. Right to Review</h4>
            <p className="mb-3">
              You can request to see all personal information we've collected about your child.
            </p>
            <p className="text-sm">
              <strong>How to request:</strong> Email privacy@glyyd.com with your child's username
              and proof of your identity.
            </p>
          </div>

          <div className="bg-gray-50 rounded-lg p-6 mb-6">
            <h4 className="text-lg font-semibold mb-3">2. Right to Delete</h4>
            <p className="mb-3">
              You can request that we delete your child's personal information at any time.
            </p>
            <p className="text-sm">
              <strong>Note:</strong> Deletion may affect your child's ability to use the platform
              and connect with coaches.
            </p>
          </div>

          <div className="bg-gray-50 rounded-lg p-6 mb-6">
            <h4 className="text-lg font-semibold mb-3">3. Right to Refuse Further Collection</h4>
            <p className="mb-3">
              You can stop us from collecting more information from your child while still allowing
              them to use existing features.
            </p>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 mb-6">
            <h4 className="text-lg font-semibold mb-3">4. NEW: Granular Consent Control</h4>
            <p className="mb-3">As of June 23, 2025, you can separately control consent for:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Basic platform use (required)</li>
              <li>Third-party data sharing (optional)</li>
              <li>Coach communications (optional)</li>
              <li>Analytics tracking (optional)</li>
            </ul>
            <p className="text-sm mt-3">
              You can withdraw consent for optional features without affecting basic platform
              access.
            </p>
          </div>

          <h3 className="text-xl font-semibold mt-6 mb-3">
            How We Protect Your Child's Information
          </h3>
          <ul className="list-disc pl-6 space-y-2 mb-6">
            <li>Encryption of all data in transit and at rest</li>
            <li>Limited access to personal information</li>
            <li>Regular security assessments</li>
            <li>No selling of personal information</li>
            <li>Age-appropriate privacy defaults</li>
          </ul>

          <h3 className="text-xl font-semibold mt-6 mb-3">Third-Party Sharing</h3>
          <p className="mb-4">With your separate consent, we may share information with:</p>
          <ul className="list-disc pl-6 space-y-2 mb-6">
            <li>
              <strong>College Coaches:</strong> For recruitment purposes
            </li>
            <li>
              <strong>Rowing Organizations:</strong> USRowing, IRA for verification
            </li>
            <li>
              <strong>Service Providers:</strong> Hosting, email, video platforms
            </li>
          </ul>
          <p className="text-sm italic">
            You can withdraw third-party sharing consent at any time without affecting your child's
            ability to use other platform features.
          </p>

          <h3 className="text-xl font-semibold mt-6 mb-3">Exercising Your Rights</h3>
          <div className="bg-rowing-navy text-white rounded-lg p-6 mb-6">
            <h4 className="text-lg font-semibold mb-3">Contact Us</h4>
            <p className="mb-3">To exercise any of your parental rights:</p>
            <ul className="space-y-2">
              <li>
                <strong>Email:</strong> privacy@glyyd.com
              </li>
              <li>
                <strong>Subject Line:</strong> "COPPA Rights Request"
              </li>
              <li>
                <strong>Include:</strong> Your child's username and your relationship
              </li>
            </ul>
            <p className="text-sm mt-3">
              We'll verify your identity and respond within 10 business days.
            </p>
          </div>

          <h3 className="text-xl font-semibold mt-6 mb-3">Identity Verification</h3>
          <p className="mb-4">
            To protect your child's privacy, we require identity verification. We may ask for:
          </p>
          <ul className="list-disc pl-6 space-y-2 mb-6">
            <li>Government-issued ID</li>
            <li>Signed consent form</li>
            <li>Credit card verification (small charge refunded)</li>
            <li>Video call verification</li>
          </ul>

          <h3 className="text-xl font-semibold mt-6 mb-3">Data Retention</h3>
          <p className="mb-4">We keep your child's information only as long as necessary:</p>
          <ul className="list-disc pl-6 space-y-2 mb-6">
            <li>Active account data: Until deletion requested</li>
            <li>Backup data: 90 days after deletion</li>
            <li>Legal compliance data: As required by law</li>
          </ul>

          <h3 className="text-xl font-semibold mt-6 mb-3">Questions or Concerns?</h3>
          <p className="mb-4">We're here to help. Contact us at:</p>
          <div className="bg-gray-100 rounded-lg p-4">
            <p>
              <strong>Email:</strong> privacy@glyyd.com
            </p>
            <p>
              <strong>Phone:</strong> [TO BE ADDED]
            </p>
            <p>
              <strong>Mail:</strong> [TO BE ADDED]
            </p>
          </div>

          <div className="mt-8 p-6 border-2 border-rowing-blue rounded-lg">
            <h3 className="font-semibold mb-2">FTC COPPA Resources</h3>
            <p className="text-sm mb-2">For more information about COPPA and your rights:</p>
            <ul className="text-sm space-y-1">
              <li>
                • Visit:{' '}
                <a
                  href="https://www.ftc.gov/tips-advice/business-center/privacy-and-security/children%27s-privacy"
                  className="text-rowing-blue hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  FTC Children's Privacy
                </a>
              </li>
              <li>• Call: 1-877-FTC-HELP</li>
              <li>
                • File a complaint:{' '}
                <a
                  href="https://www.ftccomplaintassistant.gov"
                  className="text-rowing-blue hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  FTC Complaint Assistant
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoppaRightsPage;
